import React, { useCallback, useEffect, useState } from 'react';
import { FaPencilAlt, FaTrashAlt } from 'react-icons/fa';
import { useToast } from '../../../hooks/toast';
import api from '../../../services/api';
import Button from '../../../components/Button';
import Loading from '../../../components/Loading';
import Paginacao from '../../../components/Paginacao';

import { IPalavraBloqueada } from '..';
import getResponseErrors from '../../../utils/getResponseErrors';

export interface IPaginacao {
  termoBusca: string;
  limite: number;
  pagina: number;
}

type Props = {
  showModal: boolean;
  setShowModal(value: boolean): void;
  setPalavraSelecionada(palavra: IPalavraBloqueada): void;
};
const ListaPalavras: React.FC<Props> = ({
  showModal,
  setShowModal,
  setPalavraSelecionada,
}) => {
  const { addToast } = useToast();
  const [paginacao, setPaginacao] = useState<IPaginacao>({
    termoBusca: '',
    pagina: 1,
    limite: 20,
  });
  const [loading, setLoading] = useState(false);
  const [totalRegistros, setTotalRegistros] = useState<number | undefined>(
    undefined,
  );
  const [palavras, setPalavras] = useState<IPalavraBloqueada[]>([]);

  useEffect(() => {
    async function buscaPalavras(): Promise<void> {
      if (paginacao && !showModal) {
        try {
          setLoading(true);

          const response = await api.get(
            `/palavraBloqueada?pagina=${paginacao.pagina}&limite=${paginacao.limite}`,
          );

          const palavrasApi: IPalavraBloqueada[] = response.data[0];

          setTotalRegistros(
            response.data[1] === 0 ? undefined : response.data[1],
          );

          setPalavras(palavrasApi);
        } catch (error) {
          const mensagem = getResponseErrors(error);
          addToast({
            type: 'error',
            title: 'Não foi possível buscar palavras bloqueadas',
            description: mensagem,
          });
          setTotalRegistros(0);
          setPalavras([]);
        } finally {
          setLoading(false);
        }
      }
    }
    if (paginacao) buscaPalavras();
  }, [paginacao, showModal, addToast]);

  const handleDeletePalavra = useCallback(
    async (id: number) => {
      setLoading(true);
      try {
        await api.delete(`/palavraBloqueada/${id}`);
        setPalavras(PalavrasState =>
          PalavrasState?.filter(palavra => palavra.id !== id),
        );
        addToast({
          type: 'success',
          title: 'Palavra excluída',
        });
      } catch (error) {
        const mensagem = getResponseErrors(error);
        addToast({
          type: 'error',
          title: 'Erro ao deletar palavra',
          description: mensagem,
        });
      } finally {
        setLoading(false);
      }
    },
    [addToast],
  );

  const handleProximaPagina = useCallback((page: number) => {
    setPaginacao(state => {
      return state && { ...state, pagina: page };
    });
  }, []);

  const handleEditar = useCallback(
    palavra => {
      setPalavraSelecionada(palavra);
      setShowModal(true);
    },
    [setShowModal, setPalavraSelecionada],
  );

  return (
    <>
      {loading && <Loading />}

      {!showModal &&
        (palavras && palavras.length > 0 ? (
          <>
            <table
              className="table table-hover table-sm table-font-small"
              style={{ borderWidth: '0px' }}
            >
              <tbody>
                <tr className="bg-light" style={{ fontSize: 'Small' }}>
                  <th scope="col">Palavra</th>
                  <th scope="col">&nbsp;</th>
                  <th scope="col">&nbsp;</th>
                </tr>
                {palavras.map((palavra: IPalavraBloqueada) => (
                  <tr
                    key={`Palavra-${palavra.id}`}
                    style={{ fontSize: 'Small', height: '30px' }}
                  >
                    <td>{palavra.palavra}</td>
                    <td className="p-0" style={{ width: '40px' }}>
                      <Button
                        type="button"
                        variant="primary-outline"
                        className="py-0"
                        title={palavra.id.toString()}
                        onClick={() => handleEditar(palavra)}
                      >
                        <FaPencilAlt />
                      </Button>
                    </td>

                    <td className="p-0" style={{ width: '40px' }}>
                      <Button
                        type="button"
                        variant="secondary-outline"
                        className="py-0"
                        title={palavra.id.toString()}
                        onClick={() => {
                          handleDeletePalavra(palavra.id);
                        }}
                      >
                        <FaTrashAlt />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <Paginacao
              limitePagina={paginacao?.limite || 15}
              paginasVizinhas={2}
              totalRegistros={totalRegistros}
              paginaInicio={paginacao?.pagina}
              handleProximaPagina={handleProximaPagina}
            />
          </>
        ) : (
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: '250px' }}
          >
            <h5>Nenhum registro encontrado.</h5>
          </div>
        ))}
    </>
  );
};

export default ListaPalavras;
